/**
 * On a rave's own address: keep the app for offline, or give the copy back — decided once the account and its
 * raves are known (`raveSlots.ts`, the user, 2026-10-30: by the raves' dates). Signed out, nothing is kept: offline
 * is for an account (2026-10-27). On the iPhone, a refused registration frees the old raves' slots and comes back.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/app/AuthProvider';
import { useTenant } from '@/app/TenantProvider';
import { endpoints } from '@/lib/api';
import { resolveTenant } from '@/lib/tenant';
import { isFramed } from '@/lib/framed';
import { dropWorker, registerServiceWorker } from './register';
import { releaseList, slotDecision, slotLimit, startRelease } from './raveSlots';
import { forgetPastRaves } from './pastRaves';

export function RaveOffline() {
  const { user, loading } = useAuth();
  const subdomain = resolveTenant().subdomain;
  const { tenant } = useTenant();
  const editionId = tenant?.editionId ?? null;
  const logoUrl = tenant?.logoUrl ?? null;
  const active = Boolean(subdomain) && !isFramed() && typeof navigator !== 'undefined' && 'serviceWorker' in navigator && !import.meta.env.DEV;
  const mine = useQuery({ queryKey: ['me', 'festivals'], queryFn: endpoints.myFestivals, enabled: active && Boolean(user), staleTime: 5 * 60_000 });
  const done = useRef(false);
  const [kept, setKept] = useState(false);
  // Past raves work online only (the user, 2026-11-02): this address deletes the copies of raves over by two days.
  useEffect(() => { if (subdomain && !isFramed()) void forgetPastRaves(); }, [subdomain]);
  useEffect(() => {
    if (!active || loading || done.current || !subdomain) return;
    if (!user) { done.current = true; void dropWorker(); return; }
    // The SERVER's answer only: a copy on the device, or no answer, decides nothing (and drops nothing).
    if (!mine.data || mine.data.fromDevice) return;
    done.current = true;
    const raves = mine.data.festivals;
    const ua = navigator.userAgent;
    if (slotDecision(subdomain, raves, Date.now(), ua) === 'drop') { void dropWorker(); return; }
    void registerServiceWorker().then(({ rejected }) => {
      // Kept for offline: the invite's QR and the tickets (code and original) are made ready now, so they open at
      // the rave with no signal although never opened online (the 0.14.76 walk).
      if (!rejected) setKept(true);
      if (!rejected || slotLimit(ua) === Infinity) return;
      const hop = startRelease(releaseList(subdomain, raves, Date.now(), ua), window.location.href);
      if (hop) window.location.replace(hop);
    });
  }, [active, loading, user, mine.data, subdomain]);
  // Once kept and the rave is known: once a page load.
  const warmed = useRef(false);
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!kept || !editionId || !userId || warmed.current) return;
    warmed.current = true;
    void warmForOffline(editionId, logoUrl, userId);
  }, [kept, editionId, logoUrl, userId]);
  return null;
}

/**
 * What a rave kept for offline needs that only the network gives — its invite (the code, drawn as a QR with the
 * rave's logo) and its tickets (each code drawn, each original on the phone). Loaded only when needed.
 */
export async function warmForOffline(editionId: string | null, logoUrl: string | null, userId: string): Promise<void> {
  if (!editionId) return;
  const [{ warmInvite, inviteOwner }, { warmTicketsForOffline }] = await Promise.all([
    import('@/lib/invites'),
    import('@/visitor/tickets/ticketStore'),
  ]);
  await Promise.all([
    warmInvite(editionId, inviteOwner(userId), logoUrl),
    warmTicketsForOffline(editionId, logoUrl).catch(() => undefined),
  ]);
}
